import type { ComponentType } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route, matchPath, href, navigate, canGoBack } from './router';
import { Home } from './routes/Home';
import { LogPage, LoadingPage, NotFoundLog } from './routes/Log';
import { EntriesPage } from './routes/Entries';
import { EntryPage } from './routes/Entry';
import { InclusionProofPage } from './routes/InclusionProof';
import { ConsistencyProofPage } from './routes/ConsistencyProof';
import { SubmitPage } from './routes/Submit';
import { SctInspectorPage } from './routes/SctInspector';
import { SettingsPage } from './routes/Settings';
import { CreditsPage } from './routes/Credits';
import { NotFound } from './routes/NotFound';
import { Icon } from './ui/components/Icon';
import { LogSidebar } from './ui/components/LogSidebar';
import { logFromRoute, logRoute } from './ui/nav';
import { ensureRegistry, registry, registryState } from './ui/state/logs';
import { settings, updateSettings } from './ui/state/settings';
import { useLogClient } from './ui/hooks/useLogClient';

export interface PageProps {
  params: Record<string, string>;
  query: URLSearchParams;
}

/*
 * URL scheme (hash routes):
 *   /logs                       all logs            /logs/:op                 one operator
 *   /logs/:op/:log              overview            /logs/:op/:log/entries    /entry/:index
 *   /logs/:op/:log/inclusion    /consistency        (/logs/:op/:log/submit → /submit?log=…)
 *   /submit /sct /settings /about                   global tools
 *   /log/:id/…                  legacy id-based links → redirected to the readable form
 */
const routes: Array<[string, ComponentType<PageProps>]> = [
  ['/', Home],
  ['/logs', Home],
  ['/logs/:op', Home],
  ['/logs/:op/:log', LogPage],
  ['/logs/:op/:log/entries', EntriesPage],
  ['/logs/:op/:log/entry/:index', EntryPage],
  ['/logs/:op/:log/inclusion', InclusionProofPage],
  ['/logs/:op/:log/consistency', ConsistencyProofPage],
  ['/logs/:op/:log/submit', SubmitRedirect],
  ['/submit', SubmitPage],
  ['/sct', SctInspectorPage],
  ['/settings', SettingsPage],
  ['/about', CreditsPage],
  ['/credits', () => <Redirect to="/about" />],
  ['/log/:id', LegacyLog],
  ['/log/:id/entries', LegacyLog],
  ['/log/:id/entry/:index', LegacyLog],
  ['/log/:id/proof/inclusion', LegacyLog],
  ['/log/:id/proof/consistency', LegacyLog],
  ['/log/:id/submit', LegacyLog],
];

/** Submitting is a global tool; a log's old submit URL opens it with that log pre-ticked. */
function SubmitRedirect({ params, query }: PageProps) {
  const { log, loading } = useLogClient(params);
  useEffect(() => {
    if (log) navigate('/submit', { ...Object.fromEntries(query.entries()), log: log.id }, true);
  }, [log]);
  if (log) return null;
  if (loading) return <LoadingPage />;
  return <NotFoundLog id={`${params.op}/${params.log}`} />;
}

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, undefined, true), [to]);
  return null;
}

/** `/log/<id or unique prefix>/…` → the readable path, keeping the query string. */
function LegacyLog({ params, query }: PageProps) {
  const st = registryState.value;
  const log = registry.value.byIdPrefix(params.id!);
  const path = route.value.path;
  const sub = path.includes('/proof/inclusion') ? 'inclusion' : path.includes('/proof/consistency') ? 'consistency' : path.includes('/entries') ? 'entries' : params.index !== undefined ? `entry/${params.index}` : path.endsWith('/submit') ? 'submit' : undefined;
  useEffect(() => {
    if (log) navigate(logRoute(log, sub), Object.fromEntries(query.entries()), true);
  }, [log, sub]);
  useEffect(() => {
    void ensureRegistry();
  }, []);
  if (log) return null;
  if (st.status === 'loading' || st.status === 'idle') return <LoadingPage />;
  return <NotFoundLog id={params.id!} />;
}

function Outlet() {
  const { path, query } = route.value;
  for (const [pattern, Component] of routes) {
    const params = matchPath(pattern, path);
    if (params) return <Component key={params.log ? `${params.op}/${params.log}` : pattern} params={params} query={query} />;
  }
  return <NotFound params={{}} query={query} />;
}

function ThemeToggle() {
  const t = settings.value.theme;
  const next = t === 'auto' ? 'light' : t === 'light' ? 'dark' : 'auto';
  return (
    <button type="button" class="btn btn-ghost btn-sm" onClick={() => updateSettings({ theme: next })} title={`Theme: ${t} (click to switch)`}>
      <Icon name={t === 'dark' ? 'moon' : 'sun'} /> <span>{t === 'auto' ? 'Auto' : t === 'light' ? 'Light' : 'Dark'}</span>
    </button>
  );
}

export function App() {
  const path = route.value.path;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    void ensureRegistry();
  }, [settings.value.sources.join(','), settings.value.customListUrl]);
  const currentId = logFromRoute(path)?.log.id ?? (path === '/submit' ? registry.value.byIdPrefix(route.value.query.get('log') ?? '')?.id : undefined);
  const active = (p: string) => (p === '/logs' ? path === '/' || path.startsWith('/logs') || path.startsWith('/log/') : path.startsWith(p)) ? 'active' : '';
  return (
    <>
      <header class="topbar">
        <button type="button" class="btn-icon sidebar-toggle" onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle log list">
          <Icon name="menu" />
        </button>
        <span class="hist-nav" aria-label="History">
          <button type="button" class="btn-icon" onClick={() => history.back()} disabled={!canGoBack.value} title="Back (Alt+←)" aria-label="Back"><Icon name="arrowLeft" /></button>
          <button type="button" class="btn-icon" onClick={() => history.forward()} title="Forward (Alt+→)" aria-label="Forward"><Icon name="arrowRight" /></button>
        </span>
        <a class="brand" href={href('/')}>
          <img class="brand-mark" src={`${import.meta.env.BASE_URL}icon-128.png`} alt="" width="26" height="26" />
          <span>Merkle Lens</span>
        </a>
        <nav class="topnav" aria-label="Main">
          <a class={active('/logs')} href={href('/logs')}>Logs</a>
          <a class={active('/submit')} href={href('/submit')}>Submit</a>
          <a class={active('/sct')} href={href('/sct')}>SCT<span class="long"> Inspector</span></a>
          <a class={active('/settings')} href={href('/settings')}>Settings</a>
        </nav>
        <div class="right">
          <ThemeToggle />
        </div>
      </header>
      <div class="workspace">
        <LogSidebar {...(currentId ? { currentId } : {})} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div class="main-col">
          <Outlet />
          <footer class="footer">
            <span>merklens v{__APP_VERSION__}</span>
            <span>Runs entirely in your browser — requests go only to the logs, whatsmychaincert.com (or your proxy).</span>
            <a href={href('/about')} class="right">About &amp; credits</a>
          </footer>
        </div>
      </div>
    </>
  );
}
